import React, { useEffect, useMemo, useState } from "react";

export interface TravellerForm {
  ConsentForm: Object | null;
  CurrencyVisitorCrewForm: Object | null;
  CustomerForm: Object | null;
  DeclarationForm: Object | null;
  HealthForm1: Object | null;
  HealthForm2: Object | null;
  HealthForm3: Object | null;
  HealthForm4: Object | null;
  HealthForm5: Object | null;
  HealthForm6: Object | null;
  HealthForm7: Object | null;
  HealthForm8: Object | null;
  IntendedResident: Object | null;
  PackingList: Object | null;
  QRCodeBase: string;
  ResaleForm: Object | null;
  ResidentForm: Object | null;
  TransitForm: Object | null;
  VisitorSpecificForm: Object | null;
  createdAt: Date;
  id: string;
  updatedAt: Date;
  ArrivalDate: Date;
}

export interface CurrentFormContextValue {
  form?: TravellerForm;

  setForm: (form: TravellerForm | undefined) => void;
}

const FORM_KEY = "EDF:CurrentForm";

export type Agency = "*" | "CQA" | "Health" | "GVB" | "None";

export const CurrentFormContext: React.Context<CurrentFormContextValue> =
  React.createContext<CurrentFormContextValue>({
    form: undefined,
    setForm: () => {},
  });

export const CurrentFormProvider: React.FunctionComponent = (props: any) => {
  const [form, SetForm] = useState<TravellerForm | undefined>(undefined);

  const conditionallyTransform =
    (transformer: (val: any) => any) => (value: any) => {
      if (typeof value === "string") {
        return transformer(value);
      } else {
        return value;
      }
    };

  const transformDate = (value: any) => new Date(value);

  const condTransformDate = conditionallyTransform(transformDate);
  const condTransformJSON = conditionallyTransform(JSON.parse);

  const buildTransformers = (): { [key: string]: (value: any) => any } => ({
    ConsentForm: condTransformJSON,
    CurrencyVisitorCrewForm: condTransformJSON,
    CustomerForm: condTransformJSON,
    DeclarationForm: condTransformJSON,
    HealthForm1: condTransformJSON,
    HealthForm2: condTransformJSON,
    HealthForm3: condTransformJSON,
    HealthForm4: condTransformJSON,
    HealthForm5: condTransformJSON,
    HealthForm6: condTransformJSON,
    HealthForm7: condTransformJSON,
    HealthForm8: condTransformJSON,
    IntendedResident: condTransformJSON,
    PackingList: condTransformJSON,
    ResaleForm: condTransformJSON,
    ResidentForm: condTransformJSON,
    TransitForm: condTransformJSON,
    VisitorSpecificForm: condTransformJSON,
    createdAt: condTransformDate,
    updatedAt: condTransformDate,
    ArrivalDate: condTransformDate,
  });
  const defaultTransform = (val: any) => val;

  const transformers = useMemo(buildTransformers, [buildTransformers]);

  useEffect(() => {
    const fromStorage = sessionStorage.getItem(FORM_KEY);
    let form;
    if (fromStorage) {
      form = JSON.parse(fromStorage, (key: string, value: any) =>
        (transformers[key] || defaultTransform)(value)
      ) as TravellerForm;
    }
    SetForm(form);
    // eslint-disable-next-line
  }, []);

  const updateFormWrapper = (form: TravellerForm | undefined) => {
    let toStore: TravellerForm | undefined;
    if (form) {
      sessionStorage.setItem(FORM_KEY, JSON.stringify(form));

      // if the form is from the API/is strings all over the shop, parse it.
      toStore = Object.entries(form).reduce(
        (result, [key, value]) => ({
          ...result,
          [key]: (transformers[key] || defaultTransform)(value),
        }),
        {}
      ) as TravellerForm;
    } else {
      sessionStorage.removeItem(FORM_KEY);
    }

    SetForm(toStore);
  };

  return (
    <CurrentFormContext.Provider value={{ form, setForm: updateFormWrapper }}>
      {props.children}
    </CurrentFormContext.Provider>
  );
};

export const CurrentFormConsumer = CurrentFormContext.Consumer;
